'use client';

import { useCallback } from 'react';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';

/**
 * DnD 정렬 그리드 공통 훅
 *
 * BackgroundImagesSection, ShortFormBackgroundSection 등
 * 드래그 앤 드롭 정렬 기능을 사용하는 컴포넌트에서 공유됩니다.
 */

interface SortableItem {
  id: string;
}

interface UseSortableGridOptions<T extends SortableItem> {
  /** 정렬 대상 아이템 목록 */
  items: T[];
  /** 정렬 변경 시 호출할 콜백 (새 순서의 ID 배열) */
  onReorder: (orderedIds: string[]) => void;
}

interface UseSortableGridReturn {
  /** DnD 센서 배열 (@dnd-kit/core sensors prop) */
  sensors: ReturnType<typeof useSensors>;
  /** DnD 드래그 종료 핸들러 (@dnd-kit/core onDragEnd prop) */
  handleDragEnd: (event: DragEndEvent) => void;
}

export function useSortableGrid<T extends SortableItem>({
  items,
  onReorder,
}: UseSortableGridOptions<T>): UseSortableGridReturn {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      const newOrder = arrayMove(items, oldIndex, newIndex);
      onReorder(newOrder.map((item) => item.id));
    },
    [items, onReorder]
  );

  return { sensors, handleDragEnd };
}

export type { UseSortableGridOptions, UseSortableGridReturn };
